"use client";

import contractsAvailableDarkImage from "@/assets/global/contracts-available-dark.webp";
import contractsAvailableImage from "@/assets/global/contracts-available.webp";
import placeBidsOrAsksDarkImage from "@/assets/global/place-bids-or-asks-dark.webp";
import placeBidsOrAsksImage from "@/assets/global/place-bids-or-asks.webp";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SmartImage } from "@/components/ui/smart-image";
import { useThemeMode } from "@/features/theme/theme-provider";

interface OrderBookModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function OrderBookModal( { isOpen, onClose }: OrderBookModalProps ) {
    const { themeMode } = useThemeMode();
    const contractsAvailableSrc = themeMode === "dark" ? contractsAvailableDarkImage : contractsAvailableImage;
    const placeBidsOrAsksSrc = themeMode === "dark" ? placeBidsOrAsksDarkImage : placeBidsOrAsksImage;

    const handleOpenChange = ( open: boolean ) => {
        if ( !open ) {
            onClose();
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
            <SheetContent
                side="bottom"
                className="p-0 gap-0 rounded-t-xl shadow-none h-[calc(var(--vh,1vh)*100-56px)] overflow-hidden max-w-[500px] mx-auto"
                showCloseButton={false}
            >
                <SheetTitle className="sr-only">Order book basics</SheetTitle>

                {/* Modal Content */}
                <div className="overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-surface-subtle z-10 flex items-center justify-between h-14 px-4 border-b border-stroke">
                        <SheetClose className="flex items-center justify-center size-6 active:bg-surface-muted rounded-lg transition-colors">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                className="size-6 text-t1"
                            >
                                <path
                                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                                    fill="currentColor"
                                    fillOpacity="0.9"
                                />
                            </svg>
                        </SheetClose>
                        <h2 className="absolute left-1/2 -translate-x-1/2 text-base font-medium text-t1 leading-6">
                            Order book basics
                        </h2>
                        <div className="w-16"></div>
                    </div>

                    {/* Content */}
                    <div className="px-4 pt-4 pb-6">
                        <div className="flex flex-col gap-4">
                            {/* Introduction */}
                            <div className="flex flex-col">
                                <p className="text-sm font-normal text-t1 leading-6 tracking-[0.15px]">
                                    The order book shows a live list of buy orders
                                </p>
                                <p className="text-sm font-normal text-t1 leading-6 tracking-[0.15px]">
                                    (bids) and sell orders (asks).
                                </p>
                            </div>

                            {/* Ask prices */}
                            <div className="flex flex-col">
                                <div className="flex flex-wrap items-start gap-1.5">
                                    <span className="text-sm font-semibold text-no leading-6">Ask prices</span>
                                    <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                        are what traders are willing to sell
                                    </p>
                                </div>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    for. You may buy contracts from them
                                </p>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    immediately or place an offer to buy for less.
                                </p>
                            </div>

                            {/* Bid prices */}
                            <div className="flex flex-col">
                                <div className="flex flex-wrap items-start gap-1.5">
                                    <span className="text-sm font-semibold text-yes leading-6">Bid prices</span>
                                    <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                        are what traders are willing to buy
                                    </p>
                                </div>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    for. You may sell contracts to them
                                </p>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    immediately or place an offer to sell for more.
                                </p>
                            </div>

                            {/* Contracts available */}
                            <div className="flex flex-col">
                                <div className="flex flex-wrap items-start gap-1.5">
                                    <span className="text-sm font-semibold text-t1 leading-6">Contracts available</span>
                                    <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                        at each price are listed to
                                    </p>
                                </div>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    the right. A higher number means it is easier
                                </p>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    to buy or sell.
                                </p>
                            </div>

                            {/* Order book screenshot */}
                            <SmartImage src={contractsAvailableSrc} alt="Order panel screenshot" />

                            {/* The spread */}
                            <div className="flex flex-col">
                                <div className="flex flex-wrap items-start gap-1.5">
                                    <span className="text-sm font-semibold text-t1 leading-6">The spread</span>
                                    <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                        is the gap between the bid and
                                    </p>
                                </div>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    ask prices. Narrower spreads mean greater
                                </p>
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    agreement between traders.
                                </p>
                            </div>

                            {/* Place bids or asks */}
                            <div className="flex flex-col">
                                <p className="text-sm font-normal text-t3 leading-6 tracking-[0.15px]">
                                    <span className="text-sm font-semibold text-t1 leading-6">Place bids or asks</span>
                                    {" "}
                                    using the limit order setting in the order panel. Your contracts may fill
                                </p>
                            </div>

                            {/* Order panel screenshot */}
                            <SmartImage src={placeBidsOrAsksSrc} alt="Order panel screenshot" />
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
